// Dashboard — DATOS REALES para los cuatro niveles. Antes las cifras estaban
// escritas a mano en este archivo (760 cooperativas, 15.200 afiliados, 6 países,
// crecimiento de capital...) sin tocar la base de datos.
//
// Fuentes:
//   • Totales y conteos     → GET organizacion/resumen (cooperativas activas,
//                             afiliados activos, aporte mensual).
//   • Crecimiento           → GET organizacion/resumen/crecimiento (nuevos
//                             afiliados y cooperativas por mes).
//   • Afiliados / comités   → GET afiliados, GET cooperativas/{id}/comites.
//   • Wallet del afiliado   → GET afiliados/{id}/wallet.
// Lo que no tiene fuente en la base de datos (capital consolidado, proyectos,
// fondo solidario...) NO se muestra: se irá agregando cuando su módulo esté
// conectado.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Apartment as ApartmentIcon,
  Business as BusinessIcon,
  Gavel as GavelIcon,
  Groups as GroupsIcon,
  Paid as PaidIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../context/AuthContext';
import useRedReal from '../hooks/useRedReal';
import { comitesApi, organizacionApi } from '../services/coopnetApi';
import { etiquetaMes, fecha, fmt, moneda, plural } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });

const StatCard = ({ title, value, subtitle, icon, color }) => {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Avatar sx={{ width: 56, height: 56, mb: 2, bgcolor: alpha(c, 0.12), color: c }}>{icon}</Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const Cabecera = ({ chip, titulo, subtitulo }) => (
  <Box sx={{ mb: 4 }}>
    <Chip label={chip} size="small" sx={{ mb: 1 }} />
    <Typography variant="h4" fontWeight="bold">
      {titulo}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      {subtitulo}
    </Typography>
  </Box>
);

const Panel = ({ titulo, children }) => (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {titulo}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const Vacio = ({ texto }) => (
  <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
    {texto}
  </Typography>
);

const Cargando = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

const GraficoBarras = ({ datos, x, series, alto = 260 }) => (
  <ResponsiveContainer width="100%" height={alto}>
    <BarChart data={datos}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey={x} />
      <YAxis allowDecimals={false} />
      <Tooltip />
      <Legend />
      {series.map(([clave, color]) => (
        <Bar key={clave} dataKey={clave} fill={color} />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

// ─────────────────────────────────────────────────────────────
// Confederación / SuperAdmin — vista global de la red
// ─────────────────────────────────────────────────────────────
const DashboardConfederacion = () => {
  const red = useRedReal();
  const [serie, setSerie] = useState([]);

  useEffect(() => {
    organizacionApi
      .crecimiento(12)
      .then((s) => setSerie(Array.isArray(s) ? s : []))
      .catch(() => setSerie([]));
  }, []);

  const crecimiento = serie.map((p) => ({ mes: etiquetaMes(p.mes), 'Nuevos afiliados': p.afiliados, 'Nuevas cooperativas': p.cooperativas }));
  const hayCrecimiento = serie.some((p) => p.afiliados > 0 || p.cooperativas > 0);

  const porPais = useMemo(() => {
    const mapa = new Map();
    red.confederaciones.forEach((c) => {
      const n = red.conteos.confederaciones.get(c.id) || {};
      const clave = c.pais || 'Sin país';
      const x = mapa.get(clave) || { pais: clave, Federaciones: 0, Cooperativas: 0 };
      x.Federaciones += n.federaciones || 0;
      x.Cooperativas += n.cooperativas || 0;
      mapa.set(clave, x);
    });
    return [...mapa.values()];
  }, [red.confederaciones, red.conteos]);

  if (red.cargando) return <Cargando />;
  const t = red.totales;

  return (
    <Box>
      <Cabecera chip="Vista global" titulo="Confederación Internacional" subtitulo="Visión global de la red de economías colaborativas" />
      {red.error && <Alert severity="error" sx={{ mb: 3 }}>{red.error}</Alert>}

      <Box sx={{ ...grid(230), mb: 3 }}>
        <StatCard title="Países" value={fmt(t.paises)} subtitle={plural(t.confederaciones, 'confederación nacional', 'confederaciones nacionales')} icon={<PublicIcon />} color="#1B5E20" />
        <StatCard title="Federaciones" value={fmt(t.federaciones)} icon={<ApartmentIcon />} color="#E65100" />
        <StatCard title="Cooperativas activas" value={fmt(t.cooperativas)} icon={<BusinessIcon />} color="#6A1B9A" />
        <StatCard title="Afiliados activos" value={fmt(t.afiliados)} subtitle={`Aporte mensual: ${moneda(t.aporteMensual)}`} icon={<GroupsIcon />} color="#2E7D32" />
      </Box>

      <Box sx={{ ...grid(420), mb: 3 }}>
        <Panel titulo="Nuevos afiliados y cooperativas por mes (últimos 12 meses)">
          {hayCrecimiento ? (
            <GraficoBarras datos={crecimiento} x="mes" series={[['Nuevos afiliados', '#2E7D32'], ['Nuevas cooperativas', '#6A1B9A']]} />
          ) : (
            <Vacio texto="Aún no hay afiliaciones ni cooperativas registradas en este período." />
          )}
        </Panel>
        <Panel titulo="Distribución por país">
          {porPais.length ? (
            <GraficoBarras datos={porPais} x="pais" series={[['Federaciones', '#E65100'], ['Cooperativas', '#6A1B9A']]} />
          ) : (
            <Vacio texto="Aún no hay confederaciones nacionales." />
          )}
        </Panel>
      </Box>

      <Panel titulo="Confederaciones nacionales">
        {red.confederaciones.length === 0 ? (
          <Vacio texto="Aún no hay confederaciones. Créalas o impórtalas desde «Administración de la Red»." />
        ) : (
          <Box sx={grid(200)}>
            {red.confederaciones.map((c) => {
              const n = red.conteos.confederaciones.get(c.id) || {};
              return (
                <Card key={c.id} variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 28 }}>{red.bandera(c.paisId, c.pais)}</Typography>
                    <Typography fontWeight={600}>{c.pais}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {c.nombre}
                    </Typography>
                    <Typography variant="caption" display="block">{plural(n.federaciones, 'federación', 'federaciones')}</Typography>
                    <Typography variant="caption" display="block">{plural(n.cooperativas, 'cooperativa', 'cooperativas')}</Typography>
                    <Typography variant="caption" display="block">{plural(n.afiliados, 'afiliado', 'afiliados')}</Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Panel>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Federación
// ─────────────────────────────────────────────────────────────
const DashboardFederacion = ({ contexto }) => {
  const red = useRedReal();
  const fed = contexto?.federacion;
  const propias = useMemo(
    () => red.cooperativas.filter((k) => k.federacionId === fed?.id).sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [red.cooperativas, fed?.id]
  );
  if (red.cargando) return <Cargando />;
  if (!fed) return <Alert severity="info">Aún no hay una federación asociada a esta vista.</Alert>;

  const n = red.conteos.federaciones.get(fed.id) || {};
  const datosGrafico = propias.filter((k) => k.activa !== false).map((k) => ({ cooperativa: k.siglas || k.nombre, Afiliados: red.conteos.cooperativas.get(k.id)?.afiliados || 0 }));

  return (
    <Box>
      <Cabecera chip="Vista federación" titulo={fed.nombre} subtitulo={[fed.especializacion, 'Panel de administración de la federación'].filter(Boolean).join(' · ')} />
      <Box sx={{ ...grid(230), mb: 3 }}>
        <StatCard title="Cooperativas activas" value={fmt(n.cooperativas)} subtitle={fed.maxCooperativas ? `Capacidad: ${fed.maxCooperativas} máximo` : undefined} icon={<BusinessIcon />} color="#6A1B9A" />
        <StatCard title="Afiliados activos" value={fmt(n.afiliados)} icon={<GroupsIcon />} color="#2E7D32" />
        <StatCard title="Aporte mensual" value={moneda(n.aporteMensual)} icon={<PaidIcon />} color="#0D47A1" />
      </Box>
      <Box sx={{ ...grid(420) }}>
        <Panel titulo="Afiliados por cooperativa">
          {datosGrafico.length ? <GraficoBarras datos={datosGrafico} x="cooperativa" series={[['Afiliados', '#2E7D32']]} /> : <Vacio texto="Aún no hay cooperativas activas." />}
        </Panel>
        <Panel titulo="Cooperativas de la federación">
          {propias.length === 0 ? (
            <Vacio texto="Esta federación aún no tiene cooperativas." />
          ) : (
            <List disablePadding>
              {propias.map((k) => {
                const c = red.conteos.cooperativas.get(k.id) || {};
                return (
                  <ListItem key={k.id} divider>
                    <ListItemText primary={k.nombre} secondary={`${plural(c.afiliados, 'afiliado', 'afiliados')} • aporte ${moneda(c.aporteMensual)}`} />
                    {k.activa === false && <Chip size="small" label="Inactiva" />}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Panel>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Cooperativa
// ─────────────────────────────────────────────────────────────
const DashboardCooperativa = ({ contexto }) => {
  const red = useRedReal();
  const coop = contexto?.cooperativa;
  const [afiliados, setAfiliados] = useState({ cargando: true, items: [] });
  const [comites, setComites] = useState(null);

  useEffect(() => {
    if (!coop?.id) return undefined;
    let vivo = true;
    organizacionApi
      .listarAfiliadosRed({ cooperativaId: coop.id, estado: 'activo', tamano: 8 })
      .then((r) => vivo && setAfiliados({ cargando: false, items: r.items || [] }))
      .catch((err) => vivo && setAfiliados({ cargando: false, items: [], error: err?.message || 'No se pudo cargar la lista de afiliados.' }));
    comitesApi
      .listarPorCooperativa(coop.id)
      .then((c) => vivo && setComites(Array.isArray(c) ? c.length : null))
      .catch(() => vivo && setComites(null));
    return () => {
      vivo = false;
    };
  }, [coop?.id]);

  if (red.cargando) return <Cargando />;
  if (!coop) return <Alert severity="info">Aún no hay una cooperativa asociada a esta vista.</Alert>;

  const n = red.conteos.cooperativas.get(coop.id) || {};
  const docs = { COMPLETO: 'Completos', PENDIENTE_CEDULA_REP: 'Falta la cédula del representante', PENDIENTE_DOCUMENTOS: 'Pendientes' }[coop.estadoDocumentos] || coop.estadoDocumentos;

  return (
    <Box>
      <Cabecera chip="Vista cooperativa" titulo={coop.nombre} subtitulo="Panel de administración de la cooperativa" />
      <Box sx={{ ...grid(230), mb: 3 }}>
        <StatCard title="Afiliados activos" value={fmt(n.afiliados)} subtitle={`Capacidad: ${fmt(coop.maxAfiliados)} máximo`} icon={<GroupsIcon />} color="#2E7D32" />
        <StatCard title="Aporte mensual" value={moneda(n.aporteMensual)} subtitle="Suma de los aportes de sus afiliados activos" icon={<PaidIcon />} color="#0D47A1" />
        {comites !== null && <StatCard title="Comités" value={fmt(comites)} icon={<GavelIcon />} color="#E65100" />}
        <StatCard title="Documentos" value={docs} subtitle={coop.nit ? `NIT ${coop.nit}` : 'Sin NIT registrado'} icon={<BusinessIcon />} color="#6A1B9A" />
      </Box>
      <Panel titulo="Afiliados de la cooperativa">
        {afiliados.cargando ? (
          <CircularProgress size={22} />
        ) : afiliados.error ? (
          <Alert severity="warning">{afiliados.error}</Alert>
        ) : afiliados.items.length === 0 ? (
          <Vacio texto="Esta cooperativa aún no tiene afiliados activos." />
        ) : (
          <List disablePadding>
            {afiliados.items.map((a) => (
              <ListItem key={a.id} divider>
                <ListItemText primary={`${a.nombre} ${a.apellido}`} secondary={`Documento ${a.documento} • afiliado desde ${fecha(a.fechaAfiliacion)}`} />
                <Typography variant="body2">{moneda(a.aporteMensual)}</Typography>
              </ListItem>
            ))}
          </List>
        )}
      </Panel>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Afiliado
// ─────────────────────────────────────────────────────────────
const DashboardAfiliado = ({ contexto }) => {
  const afiliado = contexto?.afiliado;
  const coop = contexto?.cooperativa;
  const [wallet, setWallet] = useState({ cargando: true, movimientos: [] });

  useEffect(() => {
    if (!afiliado?.id) return undefined;
    let vivo = true;
    organizacionApi
      .walletAfiliado(afiliado.id)
      .then((w) => vivo && setWallet({ cargando: false, movimientos: Array.isArray(w?.movimientos) ? w.movimientos : [] }))
      .catch(() => vivo && setWallet({ cargando: false, movimientos: [] }));
    return () => {
      vivo = false;
    };
  }, [afiliado?.id]);

  if (!afiliado) return <Alert severity="info">Tu usuario aún no está vinculado a un afiliado.</Alert>;
  const g = (o, k) => o?.[k] ?? o?.[k.charAt(0).toUpperCase() + k.slice(1)];

  return (
    <Box>
      <Cabecera chip="Vista afiliado" titulo={`${afiliado.nombre} ${afiliado.apellido}`} subtitulo={coop?.nombre || ''} />
      <Box sx={{ ...grid(230), mb: 3 }}>
        <StatCard title="Aporte mensual" value={moneda(afiliado.aporteMensual)} icon={<PaidIcon />} color="#0D47A1" />
        <StatCard title="Afiliado desde" value={fecha(afiliado.fechaAfiliacion)} subtitle={`Estado: ${afiliado.estado}`} icon={<GroupsIcon />} color="#2E7D32" />
      </Box>
      <Panel titulo="Mis movimientos">
        {wallet.cargando ? (
          <CircularProgress size={22} />
        ) : wallet.movimientos.length === 0 ? (
          <Vacio texto="Aún no tienes movimientos registrados." />
        ) : (
          <List disablePadding>
            {wallet.movimientos.slice(0, 10).map((m, i) => (
              <React.Fragment key={g(m, 'comprobanteId') ?? i}>
                <ListItem>
                  <ListItemText primary={g(m, 'concepto') || 'Movimiento'} secondary={fecha(g(m, 'fecha'))} />
                </ListItem>
                {i < Math.min(wallet.movimientos.length, 10) - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Panel>
    </Box>
  );
};

const Dashboard = () => {
  const { user, contexto, NIVELES } = useAuth();

  switch (user?.nivel) {
    case NIVELES.CONFEDERACION:
      return <DashboardConfederacion />;
    case NIVELES.FEDERACION:
      return <DashboardFederacion contexto={contexto} />;
    case NIVELES.COOPERATIVA:
      return <DashboardCooperativa contexto={contexto} />;
    case NIVELES.AFILIADO:
    default:
      return <DashboardAfiliado contexto={contexto} />;
  }
};

export default Dashboard;
